<template>
  <v-container
    fluid
    class="pa-0"
  >
    <v-row
      v-if="hasAccessToSettingsModule"
      class="justify-center align-start"
    >
      <v-col>
        <!-- Header -->
        <v-row class="justify-space-between px-2 mb-4">
          <v-col class="col-auto">
            <span class="headline font-weight-bold">Settings</span>
          </v-col>

          <v-col class="col-auto">
            <v-row>
              <v-col>
                <v-btn
                  color="primary"
                  outlined
                  class="text-none"
                  :loading="isLoading"
                  @click="exportSettings"
                >
                  <font-awesome-icon
                    :icon="['fas', 'file-export']"
                    size="sm"
                    class="mr-2"
                  />
                  Export
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col>
            <v-divider />
          </v-col>
        </v-row>

        <!-- Error Alert -->
        <v-row v-if="hasError">
          <v-col>
            <v-alert
              type="error"
              dismissible
              @input="clearError"
            >
              {{ errorMessage }}
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <!-- Navigation Sidebar -->
          <v-col
            class="col-auto"
            style="min-width: 300px;"
          >
            <v-card class="elevation-0">
              <v-card-title>
                <v-col class="py-0">
                  <span class="subtitle-2">Settings</span>
                </v-col>
              </v-card-title>

              <v-card-text>
                <!-- User Profile Summary -->
                <v-row class="mb-4">
                  <v-col>
                    <v-avatar
                      size="48"
                      color="primary"
                      class="mr-3"
                    >
                      <span class="white--text text-h6">
                        {{ userInitials }}
                      </span>
                    </v-avatar>
                    <div class="d-inline-block">
                      <div class="font-weight-medium">
                        {{ fullName }}
                      </div>
                      <div class="caption text--secondary">
                        {{ userEmail }}
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="mb-4" />

                <!-- Navigation Menu -->
                <v-list dense>
                  <v-list-item-group
                    v-model="selectedTabIndex"
                    color="primary"
                  >
                    <v-list-item
                      v-for="(tab, index) in settingsTabs"
                      :key="tab.value"
                      :value="index"
                      @click="selectTab(tab.value)"
                    >
                      <v-list-item-icon>
                        <font-awesome-icon :icon="tab.icon" />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ tab.title }}</v-list-item-title>
                        <v-list-item-subtitle v-if="tab.subtitle">
                          {{ tab.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="tab.badge">
                        <v-chip
                          small
                          :color="tab.badgeColor"
                          text-color="white"
                        >
                          {{ tab.badge }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Main Content Area -->
          <v-col>
            <v-card class="elevation-0">
              <v-card-text class="pa-6">
                <!-- Loading State -->
                <div
                  v-if="isLoading"
                  class="text-center py-8"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                  />
                  <div class="mt-4 text--secondary">
                    Loading settings...
                  </div>
                </div>

                <!-- Settings Content -->
                <div v-else>
                  <!-- Profile Tab -->
                  <settings-profile
                    v-show="selectedTab === 'profile'"
                    :profile="profile"
                    :department="userDepartment"
                    :loading-department="loadingTeams"
                    :loading="isLoading"
                    @update="updateProfile"
                  />

                  <!-- Preferences Tab -->
                  <settings-preferences
                    v-show="selectedTab === 'preferences'"
                    :preferences="preferences"
                    :loading="isLoading"
                    @update="updatePreferences"
                    @theme-change="updateTheme"
                  />

                  <!-- Notifications Tab -->
                  <settings-notifications
                    v-show="selectedTab === 'notifications'"
                    :notifications="notifications"
                    :loading="isLoading"
                    @update="updateNotifications"
                  />

                  <!-- Security Tab -->
                  <settings-security
                    v-show="selectedTab === 'security'"
                    :security="security"
                    :loading="isLoading"
                    @update="updateSecurity"
                  />

                  <!-- Privacy Tab -->
                  <settings-privacy
                    v-show="selectedTab === 'privacy'"
                    :privacy="privacy"
                    :loading="isLoading"
                    @update="updatePrivacy"
                  />

                  <!-- Permissions Tab -->
                  <settings-permissions
                    v-show="selectedTab === 'permissions'"
                    :loading="isLoading"
                  />

                  <!-- Demo Mode Tab (for internal users) -->
                  <settings-demo
                    v-show="selectedTab === 'demo' && showDemoSettings"
                    :demo-mode="demoMode"
                    :loading="isLoading"
                    @update="updateDemoMode"
                  />

                  <!-- Alias Tab (for users with permissions) -->
                  <settings-alias
                    v-show="selectedTab === 'alias' && showAliasSettings"
                    :alias-settings="aliasSettings"
                    :loading="isLoading"
                    @update="updateAliasSettings"
                  />

                  <!-- Debug Tab (for developers) -->
                  <settings-debug
                    v-show="selectedTab === 'debug' && showDebugSettings"
                    :current-user="currentUser"
                    :loading="isLoading"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- No Access Message -->
    <v-row
      v-else
      class="justify-center align-center"
      style="min-height: 400px;"
    >
      <v-col class="text-center">
        <font-awesome-icon
          :icon="['fas', 'gear']"
          size="4x"
          class="mb-4 grey--text text--lighten-1"
        />
        <h3 class="text-h5 font-weight-regular mb-2">
          Settings Not Available
        </h3>
        <p class="text--secondary">
          You don't have permission to access the settings module.
        </p>
      </v-col>
    </v-row>

    <!-- Import Settings Dialog -->
    <v-dialog
      v-model="showImportDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title>Import Settings</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="importFile"
            accept=".json"
            label="Select settings file"
            show-size
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showImportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!importFile"
            :loading="isLoading"
            @click="importSettings"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import tabQuerySync from '@/mixins/tabQuerySync'

// Import settings components
import SettingsProfile from '../components/SettingsProfile.vue'
import SettingsPreferences from '../components/SettingsPreferences.vue'
import SettingsNotifications from '../components/SettingsNotifications.vue'
import SettingsSecurity from '../components/SettingsSecurity.vue'
import SettingsPrivacy from '../components/SettingsPrivacy.vue'
import SettingsDemo from '../components/SettingsDemo.vue'
import SettingsAlias from '../components/SettingsAlias.vue'
import SettingsDebug from '../components/SettingsDebug.vue'
import SettingsPermissions from '../components/SettingsPermissions.vue'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapSettingsState, mapGetters: mapSettingsGetters, mapActions: mapSettingsActions } = createNamespacedHelpers('settings')

export default {
  name: 'SettingsApp',
  components: {
    SettingsProfile,
    SettingsPreferences,
    SettingsNotifications,
    SettingsSecurity,
    SettingsPrivacy,
    SettingsDemo,
    SettingsAlias,
    SettingsDebug,
    SettingsPermissions,
  },
  mixins: [tabQuerySync],
  data() {
    return {
      showImportDialog: false,
      importFile: null,
      tabQueryConfig: {
        queryParam: 'tab',
        validValues: [
          'profile', 'permissions', 'preferences', 'notifications',
          'security', 'privacy', 'demo', 'alias', 'debug',
        ],
        defaultValue: 'profile',
      },
    }
  },
  computed: {
    ...mapAuthGetters([
      'hasAccessToSettingsModule',
      'hasAliasPermission',
      'isInternalUser',
      'hasDemoModePermissions',
      'hasCustomRoleDev',
      'currentUser',
    ]),
    ...mapSettingsState([
      'selectedTab',
    ]),
    ...mapSettingsGetters([
      'isLoading',
      'hasError',
      'errorMessage',
      'userSettings',
      'profile',
      'preferences',
      'notifications',
      'security',
      'privacy',
      'demoMode',
      'aliasSettings',
      'fullName',
      'userEmail',
      'userDepartment',
      'loadingTeams',
    ]),

    userInitials() {
      const { profile } = this
      const first = profile.firstName ? profile.firstName.charAt(0) : ''
      const last = profile.lastName ? profile.lastName.charAt(0) : ''
      return (first + last).toUpperCase() || '?'
    },

    selectedTabIndex: {
      get() {
        // Use query param tab value from mixin
        const tabValue = this.currentTabValue
        const index = this.settingsTabs.findIndex((tab) => tab.value === tabValue)
        return index >= 0 ? index : 0
      },
      set(index) {
        const tab = this.settingsTabs[index]
        if (tab) {
          this.selectTab(tab.value)
        }
      },
    },

    showDemoSettings() {
      return this.isInternalUser && this.hasDemoModePermissions
    },

    showAliasSettings() {
      return this.hasAliasPermission
    },

    showDebugSettings() {
      return this.hasCustomRoleDev
    },

    settingsTabs() {
      const tabs = [
        {
          title: 'Profile',
          subtitle: 'Personal information',
          value: 'profile',
          icon: ['fas', 'user'],
        },
        {
          title: 'Permissions',
          subtitle: 'Access & roles',
          value: 'permissions',
          icon: ['fas', 'shield-halved'],
        },
        {
          title: 'Preferences',
          subtitle: 'Theme and display',
          value: 'preferences',
          icon: ['fas', 'sliders'],
        },
        {
          title: 'Notifications',
          subtitle: 'Email and alerts',
          value: 'notifications',
          icon: ['fas', 'bell'],
        },
        {
          title: 'Security',
          subtitle: 'Password and sessions',
          value: 'security',
          icon: ['fas', 'lock'],
        },
        {
          title: 'Privacy',
          subtitle: 'Data sharing',
          value: 'privacy',
          icon: ['fas', 'eye-slash'],
        },
      ]

      // Add demo mode tab for internal users
      if (this.showDemoSettings) {
        tabs.push({
          title: 'Demo Mode',
          subtitle: 'Development features',
          value: 'demo',
          icon: ['fas', 'flask'],
        })
      }

      // Add alias tab for users with permission
      if (this.showAliasSettings) {
        tabs.push({
          title: 'Alias',
          subtitle: 'User impersonation',
          value: 'alias',
          icon: ['fas', 'user-secret'],
        })
      }

      // Add debug tab for developers
      if (this.showDebugSettings) {
        tabs.push({
          title: 'Debug',
          subtitle: 'Developer tools',
          value: 'debug',
          icon: ['fas', 'bug'],
          badge: 'DEV',
          badgeColor: 'purple',
        })
      }

      return tabs
    },
  },

  watch: {
    currentTabValue(newTab) {
      // Sync store when URL changes (browser back/forward)
      if (newTab && newTab !== this.selectedTab) {
        this.showSettingsView(newTab)
      }
    },
  },

  async created() {
    if (this.hasAccessToSettingsModule) {
      try {
        // Self-managed view: load own data
        await this.$store.dispatch('tools/setView', 'settings')
        await this.initializeSettings()

        // Initialize tab from URL query param
        const tabValue = this.currentTabValue
        if (tabValue) {
          this.showSettingsView(tabValue)
        }
      } catch (error) {
        this.$log.error('Failed to initialize settings:', error)
      }
    }
  },

  methods: {
    ...mapSettingsActions([
      'initializeSettings',
      'updateProfile',
      'updatePreferences',
      'updateTheme',
      'updateNotifications',
      'updateSecurity',
      'updatePrivacy',
      'updateDemoMode',
      'updateAliasSettings',
      'exportSettings',
      'importSettings',
      'clearError',
      'showSettingsView',
    ]),

    selectTab(tabValue) {
      // Sync to store for v-show directives
      this.showSettingsView(tabValue)

      // Sync to URL query param
      const { queryParam } = this.tabQueryConfig
      if (this.$route.query[queryParam] !== tabValue) {
        this.$router.push({
          query: { ...this.$route.query, [queryParam]: tabValue },
        }).catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
      }
    },

    async handleImportSettings() {
      if (!this.importFile) return

      try {
        const fileContent = await this.readFileAsText(this.importFile)
        const importData = JSON.parse(fileContent)

        await this.importSettings(importData)

        this.showImportDialog = false
        this.importFile = null
        this.$store.dispatch('messages/showSuccess', 'Settings imported successfully', { root: true })
      } catch (error) {
        this.$store.dispatch('messages/showError', 'Failed to import settings', { root: true })
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file)
      })
    },

    openImportDialog() {
      this.showImportDialog = true
    },
  },
}
</script>

<style scoped>
.settings-sidebar {
  border-right: 1px solid #e0e0e0;
}

.settings-content {
  min-height: 600px;
}

.user-avatar {
  display: inline-block;
  vertical-align: middle;
}

.nav-item-badge {
  font-size: 0.75rem;
}
</style>
